import type { ColumnsType } from 'antd/es/table';
import { InputNumber } from 'antd';
import dayjs from 'dayjs';
import type {
  CommissionAgentPreview,
  CommissionItem,
  CommissionLeadPreviewLine,
} from './types';

const moneyFormat = (v: number) =>
  v.toLocaleString('en-US', { minimumFractionDigits: 2 });

export function getCommissionLeadPreviewColumns(
  onPaidChange: (leadId: string, paid: number) => void
): ColumnsType<CommissionLeadPreviewLine> {
  return [
    {
      title: 'Lead Title',
      dataIndex: 'leadTitle',
      key: 'leadTitle',
    },
    {
      title: 'Closing Amount',
      dataIndex: 'closingAmount',
      key: 'closingAmount',
      align: 'right',
      render: (v: number) => moneyFormat(v),
    },
    {
      title: 'Closing Date',
      dataIndex: 'closingDate',
      key: 'closingDate',
      render: (v: string | null) => (v ? dayjs(v).format('YYYY/MM/DD') : '-'),
    },
    {
      title: 'Rate (%)',
      dataIndex: 'commissionRate',
      key: 'commissionRate',
      align: 'right',
      render: (v: number) => `${v}%`,
    },
    {
      title: 'Original',
      dataIndex: 'originalAmount',
      key: 'originalAmount',
      align: 'right',
      render: (v: number) => moneyFormat(v),
    },
    {
      title: 'Paid',
      dataIndex: 'paidAmount',
      key: 'paidAmount',
      align: 'right',
      render: (v: number, record) => (
        <InputNumber<number>
          min={0}
          max={record.originalAmount}
          step={0.01}
          value={v}
          onChange={(val) => onPaidChange(record.leadId, val ?? 0)}
          style={{ width: 140 }}
        />
      ),
    },
  ];
}

export const commissionAgentPreviewColumns: ColumnsType<CommissionAgentPreview> =
  [
    {
      title: 'Agent',
      dataIndex: 'contactName',
      key: 'contactName',
    },
    {
      title: 'Leads',
      dataIndex: 'leadCount',
      key: 'leadCount',
      align: 'right',
    },
    {
      title: 'Original Total',
      dataIndex: 'originalTotal',
      key: 'originalTotal',
      align: 'right',
      render: (v: number) => moneyFormat(v),
    },
    {
      title: 'Paid Total',
      dataIndex: 'paidTotal',
      key: 'paidTotal',
      align: 'right',
      render: (v: number) => moneyFormat(v),
    },
    {
      title: 'Last Disbursed',
      dataIndex: 'lastDisbursedAt',
      key: 'lastDisbursedAt',
      render: (v: string | null) =>
        v ? dayjs(v).format('YYYY/MM/DD HH:mm') : '—',
    },
    {
      title: 'Effective Period',
      key: 'effectivePeriod',
      render: (_, record) =>
        `${dayjs(record.effectiveDateFrom).format('YYYY/MM/DD')} – ${dayjs(record.effectiveDateTo).format('YYYY/MM/DD')}`,
    },
  ];

type CommissionLineFields = Pick<
  CommissionItem,
  | 'contactName'
  | 'leadTitle'
  | 'closingAmount'
  | 'closingDate'
  | 'commissionRate'
  | 'originalCommissionAmount'
  | 'commissionAmount'
>;

function getCommissionBaseColumns<
  T extends CommissionLineFields,
>(): ColumnsType<T> {
  return [
    {
      title: 'Agent Name',
      dataIndex: 'contactName',
      key: 'contactName',
    },
    {
      title: 'Lead Title',
      dataIndex: 'leadTitle',
      key: 'leadTitle',
    },
    {
      title: 'Closing Amount',
      dataIndex: 'closingAmount',
      key: 'closingAmount',
      align: 'right',
      render: (v: number) => moneyFormat(v),
    },
    {
      title: 'Closing Date',
      dataIndex: 'closingDate',
      key: 'closingDate',
      render: (v: string | null) => {
        if (!v) return '-';
        return dayjs(v).format('YYYY/MM/DD');
      },
    },
    {
      title: 'Rate (%)',
      dataIndex: 'commissionRate',
      key: 'commissionRate',
      align: 'right',
      render: (v: number) => `${v}%`,
    },
    {
      title: 'Original',
      dataIndex: 'originalCommissionAmount',
      key: 'originalCommissionAmount',
      align: 'right',
      render: (v: number) => moneyFormat(v),
    },
    {
      title: 'Paid',
      dataIndex: 'commissionAmount',
      key: 'commissionAmount',
      align: 'right',
      render: (v: number) => moneyFormat(v),
    },
  ];
}

export const commissionReportColumns: ColumnsType<CommissionItem> = [
  ...getCommissionBaseColumns<CommissionItem>(),
  {
    title: 'Paid at',
    dataIndex: 'calculatedAtUtc',
    key: 'calculatedAtUtc',
    render: (v: string) => {
      const d = dayjs(v);
      return (
        <span title={d.format('YYYY/MM/DD HH:mm:ss')}>
          {d.format('YYYY/MM/DD HH:mm')}
        </span>
      );
    },
  },
];
